import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./admin.css";
import { adminFetch, adminLogout, isManagerMode } from "./adminApi";

/**
 * AdminShell — shared chrome for every redesigned admin screen.
 * Charcoal grouped-nav sidebar + sticky white page header + warm-paper main.
 * Also mounts the `.uma` scope wrapper (all admin CSS lives under `.uma`).
 * The AdminToastProvider is mounted ONCE globally in App.js (pages call
 * useAdminToast() above this shell in the tree, so it must live higher).
 *
 * Props:
 *   active     string — nav key of the current screen:
 *              "dashboard" | "analytics" | "orders" | "products" |
 *              "categories" | "bookings" | "live-dashboard" |
 *              "blackout-dates" | "app-notifications" | "users" | "settings"
 *   pageClass  string — page scope class, e.g. "pg-orders" (page CSS is
 *              written as `.uma .pg-orders ...`)
 *   eyebrow    string — mono eyebrow above the title (e.g. "Commerce")
 *   title      string — page title (e.g. "Manage Orders")
 *   subtitle   string/node — small grey text next to the actions
 *              (e.g. "1,248 total orders"); hidden ≤860px
 *   actions    node — right-side header slot (refresh button, primary CTA…)
 *   chromeless boolean — when true renders ONLY the .uma wrapper +
 *              AdminToastProvider (no sidebar/header). The Live Dashboard
 *              uses this: it paints its own full-viewport UI.
 *
 * Manager mode (localStorage.managerMode === "true", set by /manager login):
 * the sidebar shows ONLY Orders and the role pill reads Order Manager —
 * mirroring the legacy orders-only manager gating.
 */

/* ---- nav icons: inline SVGs copied from the design prototypes.
       stroke comes from CSS (.adm-navlink svg / .is-active svg). ---- */
const svgProps = {
  viewBox: "0 0 24 24",
  width: 18,
  height: 18,
  fill: "none",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const NavIcons = {
  dashboard: (
    <svg {...svgProps}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  analytics: (
    <svg {...svgProps}>
      <line x1="4" y1="20" x2="4" y2="12" />
      <line x1="10" y1="20" x2="10" y2="4" />
      <line x1="16" y1="20" x2="16" y2="9" />
      <line x1="21" y1="20" x2="3" y2="20" />
    </svg>
  ),
  orders: (
    <svg {...svgProps}>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
    </svg>
  ),
  products: (
    <svg {...svgProps}>
      <path d="M12 3l8 7-8 11-8-11z" />
    </svg>
  ),
  categories: (
    <svg {...svgProps}>
      <path d="M20.5 12.5l-8 8-9-9V4.5h7z" />
      <circle cx="7.8" cy="7.8" r="1.4" />
    </svg>
  ),
  bookings: (
    <svg {...svgProps}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M9 6v12" strokeDasharray="2 2" />
    </svg>
  ),
  "live-dashboard": (
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="2.5" />
      <path d="M7.5 7.5a6 6 0 000 9M16.5 7.5a6 6 0 010 9" />
    </svg>
  ),
  "blackout-dates": (
    <svg {...svgProps}>
      <rect x="3" y="4.5" width="18" height="16.5" rx="2" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4M9.5 13.5l5 4M14.5 13.5l-5 4" />
    </svg>
  ),
  users: (
    <svg {...svgProps}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20a5.5 6 0 0111 0" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M15.5 20a5 5 0 016.5-4.3" />
    </svg>
  ),
  settings: (
    <svg {...svgProps}>
      <path d="M4 6h9M18 6h2M4 12h2M11 12h9M4 18h7M16 18h4" />
      <circle cx="15.5" cy="6" r="2" />
      <circle cx="8.5" cy="12" r="2" />
      <circle cx="13.5" cy="18" r="2" />
    </svg>
  ),
  "app-notifications": (
    <svg {...svgProps}>
      <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </svg>
  ),
};

const LogoutIcon = (
  <svg {...svgProps} width={18} height={18}>
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { key: "dashboard", label: "Dashboard", to: "/admin/dashboard" },
      { key: "analytics", label: "Analytics", to: "/admin/analytics" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { key: "orders", label: "Orders", to: "/admin/orders" },
      { key: "products", label: "Products", to: "/admin/products" },
      { key: "categories", label: "Categories", to: "/admin/categories" },
    ],
  },
  {
    label: "Tours",
    items: [
      { key: "bookings", label: "Bookings", to: "/admin/bookings" },
      {
        key: "live-dashboard",
        label: "Live Dashboard",
        to: "/admin/live-dashboard",
      },
      {
        key: "blackout-dates",
        label: "Blackout Dates",
        to: "/admin/blackout-dates",
      },
    ],
  },
  {
    label: "App",
    items: [
      {
        key: "app-notifications",
        label: "App Notifications",
        to: "/admin/app-notifications",
      },
    ],
  },
  {
    label: "System",
    items: [
      { key: "users", label: "Users", to: "/admin/users" },
      { key: "settings", label: "Settings", to: "/admin/settings" },
    ],
  },
];

/* Managers (orders-only portal) see just Orders — legacy manager gating. */
const MANAGER_SECTIONS = [
  {
    label: "Commerce",
    items: [{ key: "orders", label: "Orders", to: "/admin/orders" }],
  },
];

const initialsOf = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");
};

const AdminShell = ({
  active,
  pageClass,
  eyebrow,
  title,
  subtitle,
  actions,
  chromeless = false,
  children,
}) => {
  const navigate = useNavigate();
  const manager = isManagerMode();
  const [adminInfo, setAdminInfo] = useState(null);

  // Header identity — same GET /api/admin/me the legacy dashboard used.
  // adminFetch also acts as the auth guard: missing/expired token or a 401
  // redirects to /admin/login (or /manager for managers).
  useEffect(() => {
    if (chromeless) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch("/api/admin/me", { navigate });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setAdminInfo(data);
        }
      } catch (err) {
        // AdminAuthError already redirected; other failures just leave the
        // fallback identity in the header.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chromeless]);

  const rootClass = "uma" + (pageClass ? ` ${pageClass}` : "");

  if (chromeless) {
    return <div className={rootClass}>{children}</div>;
  }

  const sections = manager ? MANAGER_SECTIONS : NAV_SECTIONS;
  const displayName =
    (adminInfo && adminInfo.name) || (manager ? "Order Manager" : "Admin");
  const displayRole = manager
    ? "Order Manager"
    : (adminInfo && adminInfo.role && adminInfo.role.replace(/_/g, " ")) ||
      "Administrator";
  const handleLogout = () => adminLogout(navigate);

  return (
    <div className={rootClass}>
      <div className="adm-shell">
          <aside className="adm-side">
            <div className="adm-brand">
              <span className="adm-brand-tile">
                <img src="/design/assets/umoona-emblem.png" alt="" />
              </span>
              <span className="adm-brand-text">
                <span className="adm-brand-name">UMOONA</span>
                <span className="adm-brand-sub">ADMIN PORTAL</span>
              </span>
            </div>

            <nav className="adm-navwrap">
              {sections.map((section) => (
                <React.Fragment key={section.label}>
                  <div className="adm-seclbl">{section.label}</div>
                  {section.items.map((item) => (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={
                        "adm-navlink" +
                        (active === item.key ? " is-active" : "")
                      }
                      aria-current={active === item.key ? "page" : undefined}
                    >
                      {NavIcons[item.key]}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </React.Fragment>
              ))}
            </nav>

            <div className="adm-foot">
              <span
                className={
                  "adm-role" + (manager ? " adm-role--manager" : "")
                }
              >
                <span className="adm-role-dot" />
                {manager ? "Order Manager" : "Administrator"}
              </span>
              <button
                type="button"
                className="adm-logout"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </aside>

          <div className="adm-body">
            <header className="adm-header">
              <div>
                {eyebrow && <div className="adm-eyebrow">{eyebrow}</div>}
                <h1 className="adm-title">{title}</h1>
              </div>
              <div className="adm-header-actions">
                {subtitle && (
                  <span className="adm-subtitle adm-hidesm">{subtitle}</span>
                )}
                {actions}
                {/* sidebar footer is hidden ≤860px — this keeps Logout reachable */}
                <button
                  type="button"
                  className="adm-iconbtn adm-logout-sm"
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                >
                  {LogoutIcon}
                </button>
                <div className="adm-avatar-wrap">
                  <span
                    className={
                      "adm-avatar" + (manager ? " adm-avatar--manager" : "")
                    }
                  >
                    {initialsOf(displayName)}
                  </span>
                  <span className="adm-avatar-meta adm-hidesm">
                    <span className="adm-avatar-name">{displayName}</span>
                    <span className="adm-avatar-role">{displayRole}</span>
                  </span>
                </div>
              </div>
            </header>

            <main className="adm-main">{children}</main>
          </div>
      </div>
    </div>
  );
};

export default AdminShell;
